/**
 * The route table.
 *
 * Replaces the monolith's `view` string plus its hand-rolled `viewHistory` stack.
 * Every screen now has a real URL, the browser Back button works because the
 * browser is doing the work, and a link into the app is shareable.
 *
 * Three structural decisions worth stating, because each was a choice:
 *
 * 1. PUBLIC vs AUTHENTICATED. `/login`, `/license/verify/:licenseId` and
 *    `/share/:slug` are mounted as siblings of the shell, not inside it.
 *    Verification has to work for someone who has never signed in — that is the
 *    entire point of a verification link — and the prototype got this wrong by
 *    rendering it as a logged-in view while its own copy invited third parties to
 *    check a licence. A share link is the same case: the recipient has no account,
 *    so the page cannot sit behind the auth gate or inside the app's chrome.
 *
 * 2. THE STUDIO IS ITS OWN PAGE. `/library/:songId` used to render inside the
 *    Library route so the drawer could slide in over the song grid. The Studio is
 *    now a full production workspace — a tool rail, a timeline, a lyric sheet and a
 *    docked transport — which does not fit in a panel over another page, so it is a
 *    sibling route that replaces the Library while you work on a track. It keeps
 *    the /library/ prefix because a track is still a thing in your library, and
 *    because every existing link to it (paths.song) stays valid.
 *
 *    `/studio` is the Studio's front door and reaches that workspace without going
 *    through the Library first; the Library has its own nav row and its own screen.
 *
 * 3. PATHS FROM §1 WITH NO SCREEN BEHIND THEM REDIRECT rather than getting a new
 *    page invented for them. `/settings/*` and `/templates` are each covered by an
 *    existing view; see the comments at their entries.
 *
 * 4. THREE AREAS ARE ENTITLEMENT-GATED — the Studio (OTO 1), the Publisher, and the two
 *    AI creation methods that Composer+ (OTO 2) sells — and each gate is here rather
 *    than in the screens it protects. See RequireStudio below for the full argument;
 *    RequirePublisher and RequireComposerPlus follow the same shape on purpose.
 */
import { lazy, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { UNKNOWN_API_ERROR_MESSAGE } from './api/errors';
import { USE_MOCK_API } from './config';
import { AppShell } from './components/layout';
import VerifiedAccessLockedState from './components/layout/VerifiedAccessLockedState';
import { SessionCheckingSplash, SessionErrorScreen } from './components/layout/SessionGateStates';
import { LicenseLockedState } from './components/license';
import { PublisherLockedState } from './components/publisher';
import { ComposerPlusLockedState, StudioLockedState } from './components/studio';
import { LoadError, LoadSkeleton, Logo } from './components/ui';
import { BadgeCheck, CirclePlus, Gift, Image as ImageIcon, Package } from 'lucide-react';
import { useEntitlementsStore } from './stores/entitlementsStore';
import { useAuthStore } from './stores/authStore';
import { c } from './styles/theme';
import Admin from './routes/Admin';
import AdminBonuses from './routes/AdminBonuses';
import AdminSupportInbox from './routes/AdminSupportInbox';
import AdminUsers from './routes/AdminUsers';
import AdminArchive from './routes/AdminArchive';
import AdminMail from './routes/AdminMail';
import AdminVault from './routes/AdminVault';
import Agreement from './routes/Agreement';
import AuthCallback from './routes/AuthCallback';
import Bonus from './routes/Bonus';
import Create from './routes/Create';
import GenreMixer from './routes/GenreMixer';
import VisualGuide from './routes/Guide';
import ImageMusic from './routes/ImageMusic';
import Legal from './routes/Legal';
import Library from './routes/Library';
import LicenseVerify from './routes/LicenseVerify';
import Licenses from './routes/Licenses';
import Login from './routes/Login';
import MusicDNA from './routes/MusicDNA';
import ResetPassword from './routes/ResetPassword';
import Reseller from './routes/Reseller';
import Onboarding from './routes/Onboarding';
import Playlist from './routes/Playlist';
import Playlists from './routes/Playlists';
import Products from './routes/Products';
import PublisherEntry from './routes/PublisherEntry';
import QuickGuide from './routes/QuickGuide';
import PublisherRelease from './routes/PublisherRelease';
import PublisherReleaseWizard from './routes/PublisherReleaseWizard';
import Settings from './routes/Settings';
import Share from './routes/Share';
import Studio from './routes/Studio';
import StudioEntry from './routes/StudioEntry';
import Support, { SupportTopic } from './routes/Support';
import Ticket from './routes/Ticket';
import Topup from './routes/Topup';
import Vault from './routes/Vault';
import VerifyEmail from './routes/VerifyEmail';
import VerifyEmailPending from './routes/VerifyEmailPending';
import VoiceCloner from './routes/VoiceCloner';
import { paths } from './routes/paths';

const Dashboard = lazy(() => import('./routes/Dashboard'));
const Explore = lazy(() => import('./routes/Explore'));

/**
 * Gate for the authenticated area.
 *
 * SIMULATED, and deliberately so: `isAuthenticated` is a client-side boolean that
 * a reload clears, exactly as in the prototype. This is a routing convenience, NOT
 * a security boundary — every screen it protects is rendered from local fixture
 * data, so there is nothing here to protect yet. Once the Python backend exists,
 * the real gate is the API rejecting unauthenticated requests; this component only
 * decides which screen to show.
 *
 * ============================================================================
 * FOUR OUTCOMES, NOT TWO — the session-restoration wiring (Batch 7 §5).
 *
 *   unknown        nothing has answered "who is signed in" → checking splash
 *   check failed   the request itself broke → retryable failure, NOT a login form
 *   signed in      → the app
 *   nobody         the server answered, and it is nobody → /login
 *
 * WHY THE SPLASH BRANCH IS NOT SIMPLY `!sessionChecked`. It is
 * `!sessionChecked && !isAuthenticated`, and the second half matters: a session that is
 * already known — set by a completed login, or by a test that arranges one — must not be
 * made to wait behind a check it does not need. Reading only `sessionChecked` would show
 * a spinner to a user who just signed in, because logging in answers the question
 * without going through `restoreSession`.
 *
 * WHY THE CHECK IS FIRED HERE. hooks/useAppData.ts deliberately does not call
 * `restoreSession` and says why: "Wiring it belongs with the route gate that consumes
 * `sessionChecked`." This is that gate. The effect runs once — guarded on
 * `sessionChecked` rather than on a ref, so a retry after a failure genuinely re-asks.
 * ============================================================================
 */
function RequireAuth() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // One field per selector call: Object.is makes an object literal a new reference on
  // every render, which is the Zustand v5 rule this codebase follows throughout.
  const sessionChecked = useAuthStore((s) => s.sessionChecked);
  const sessionError = useAuthStore((s) => s.sessionError);
  const restoreSession = useAuthStore((s) => s.restoreSession);
  const navigate = useNavigate();

  /*
    Asks once, on mount, unless the answer is already in hand.

    `void` because the action never rejects by contract (see authStore.restoreSession) —
    there is no rejection here to handle, and awaiting it in an effect would need a
    cancellation dance for no benefit.
  */
  useEffect(() => {
    if (!sessionChecked && !isAuthenticated) void restoreSession();
  }, [sessionChecked, isAuthenticated, restoreSession]);

  // The check broke. Offer a retry rather than asserting that nobody is signed in —
  // see components/layout/SessionGateStates.tsx for why that distinction is the point.
  if (sessionError && !isAuthenticated) {
    return (
      <SessionErrorScreen
        message={sessionError.message}
        onRetry={() => void restoreSession()}
        onSignIn={() => navigate(paths.login, { replace: true })}
      />
    );
  }

  // Nothing has answered yet. NOT a login form: that would be the flash-of-login-screen
  // and the lost destination URL that `sessionChecked` was added to prevent.
  if (!sessionChecked && !isAuthenticated) return <SessionCheckingSplash />;

  // `replace` so a bounced visit does not leave a dead entry in the history stack
  // that Back would land on again.
  return isAuthenticated ? <Outlet /> : <Navigate to={paths.login} replace />;
}

/**
 * Gate for the base product itself — authenticated, but not authorized to enter the app.
 *
 * `appAccess` is the server-authoritative answer to "may this account use Lyrixa at all?".
 * It is intentionally separate from the optional product entitlements below: a signed-in
 * account may be known to auth and still not have a verified purchase on that exact email.
 * In that state the app stays locked and only the upgrade/purchase page remains reachable.
 */
function RequireAppAccess() {
  return <AppShell />;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const isAdmin = useAuthStore((s) => s.user?.isAdmin === true);
  return isAdmin ? <>{children}</> : <Navigate to={paths.dashboard} replace />;
}

function RequireVerifiedAccess({ locked }: { locked: React.ReactNode }) {
  const isAdmin = useAuthStore((s) => s.user?.isAdmin === true);
  const appAccess = useAuthStore((s) => s.appAccess);

  return isAdmin || appAccess === 'granted' ? <Outlet /> : <>{locked}</>;
}

/**
 * The three states an entitlement gate has besides "owned" and "not owned".
 *
 * ============================================================================
 * A PAYWALL MUST NOT BE THE ANSWER TO A QUESTION NOBODY ASKED YET. Each gate below read
 * one boolean and had two outcomes, and `entitlementsStore` initialises to
 * `EMPTY_ENTITLEMENT` — every flag false. With a backend configured, `useAppData` fires
 * `loadEntitlements()` from `AppShell`, which mounts INSIDE `RequireAuth` — so for the
 * duration of that read, and permanently if it fails, a paying account was shown
 * "Studio access requires the Studio plan" and invited to buy what it already owned.
 *
 * That is the exact defect `entitlementsStore.load`'s own docstring describes — "'the
 * server says this account owns nothing' and 'we could not ask the server'… a paywall
 * built on the second is a paywall that appears during an outage" — and it says the read
 * state cannot fix the SERVER-side half. This is the CLIENT-side half, which it can: the
 * screen stops asserting a refusal it has no answer for. Nothing here is access control,
 * for every reason the three gates below already state.
 *
 * THE SHAPE IS `RequireAuth`'s, deliberately. That gate has four outcomes on
 * `sessionChecked`/`sessionError` and this file argues at length for why the third and
 * fourth are necessary; the argument transfers whole, with `load` in place of the two
 * session fields. Retry is offered for the same reason it is offered there.
 *
 * NOT A SPLASH. `SessionCheckingSplash` replaces the whole app because nothing is known
 * about who is signed in; here the shell, the sidebar and the nav are all correct and it
 * is one area of one screen that is unresolved. So the in-shell `LoadSkeleton`/`LoadError`
 * are used — the same two components the collection screens use, for the same reason
 * components/ui/LoadStates.tsx gives for being shared.
 *
 * `owned` STILL WINS FIRST, which matters in two directions. A refresh over an account
 * that already owns the product must not blank the workspace — the same rule
 * `isFirstLoad(load, hasData)` encodes for a collection — and in mock mode `load` never
 * leaves `idle`, so nothing about today's behaviour changes.
 * ============================================================================
 */
function EntitlementGate({
  owned,
  locked,
  name,
}: {
  owned: boolean;
  /** The paywall, rendered only once a read has actually said "not owned". */
  locked: React.ReactNode;
  /** Names the area in the loading and failure copy, e.g. "Studio". */
  name: string;
}) {
  const load = useEntitlementsStore((s) => s.load);
  const loadEntitlements = useEntitlementsStore((s) => s.loadEntitlements);

  // Owned first: a refresh must not take away access that is already established.
  if (owned) return <Outlet />;

  if (load.status === 'loading' || (!USE_MOCK_API && load.status === 'idle')) {
    return <LoadSkeleton rows={2} rowHeight={80} label={`Checking your ${name} access…`} />;
  }

  if (load.status === 'error') {
    /*
      NOT the paywall, and not the workspace either. "You have not bought this" is a
      claim about the account; this says only that the question could not be asked.
      Naming the alternative matters — someone who does own the plan needs to know the
      screen is wrong about them rather than concluding their purchase was lost.
    */
    return (
      <LoadError
        message={load.error?.message ?? UNKNOWN_API_ERROR_MESSAGE}
        onRetry={() => void loadEntitlements()}
      >
        <p style={{ fontSize: 12, color: c.text2, margin: '6px 0 0', lineHeight: 1.55 }}>
          We could not confirm what your account owns, so {name} is not shown. This is not a
          statement that you do not have it — nothing has changed about your purchases.
        </p>
      </LoadError>
    );
  }

  // 'ready' with the flag false is a real refusal, and mock-mode tests still treat 'idle'
  // as "the fixture flags in the store are the answer".
  return <>{locked}</>;
}

/**
 * Gate for the Studio, which is a purchased product (OTO 1).
 *
 * WHY IT IS ONE ROUTE-LEVEL CHECK. There are two Studio URLs (`/studio`, which
 * resolves a song, and `/library/:songId`, the workspace) and at least seven controls
 * across five screens that navigate to one of them: the composer's result card button
 * and its More menu, the Genre Mixer's result card, every Library row, every playlist
 * row, and the workspace's own library strip. Checking the entitlement at each of them
 * would be seven copies of one rule, and the first one anybody forgot to add — or the
 * next surface that links to a song — would be a hole. A pasted URL is not covered by
 * any of them at all. So the refusal lives at the destination, where every path
 * necessarily arrives, and the individual controls are free to stay simple links.
 *
 * The controls themselves are NOT hidden or disabled. They open the Studio's own
 * screen, which explains what the plan unlocks and offers it — the same convention the
 * Commercial License and the DFY Vault rows already follow, where the padlock in the
 * sidebar marks the destination and the destination does the refusing. Hiding them
 * would also mean hiding a song's primary action from the Library, which reads as the
 * feature being broken rather than unbought.
 *
 * READS THE STORE, and only the store. `useEntitlementsStore` is the single source for
 * `studio`; nothing here infers access from a route, a prop, a song or a local flag,
 * so buying the plan unlocks the workspace on the spot with no other state to sync.
 *
 * NOT A SECURITY BOUNDARY. The flag is client-side and editable from devtools, and the
 * Studio actions it protects are simulated locally, so there is nothing here to steal
 * yet; when those actions call a real API, the API is what has to refuse. See the
 * header of stores/entitlementsStore.ts. This gate is the honest client-side half:
 * every UI path into the workspace goes through it.
 */
function RequireStudio() {
  const studio = useEntitlementsStore((s) => s.studio);
  // A render, not a redirect. Bouncing to /products would lose the URL the user
  // clicked, and a deep link into a track someone shared with them would silently
  // become a shopping page with no indication of what it replaced.
  return <EntitlementGate owned={studio} locked={<StudioLockedRoute />} name="Studio" />;
}

/**
 * The locked Studio screen, wired to the purchase flow.
 *
 * Split out so StudioLockedState itself stays presentational and testable without a
 * router. `paths.upgrade` rather than `paths.products`: both render the Products page,
 * and "upgrade" is the URL the app's other upgrade prompts use.
 */
function StudioLockedRoute() {
  const navigate = useNavigate();
  return <StudioLockedState onGetStudio={() => navigate(paths.upgrade)} />;
}

/**
 * Gate for the Publisher, which is a purchased plan ($47).
 *
 * SAME SHAPE AS RequireStudio, deliberately — §5 says to use the existing entitlement
 * architecture rather than a second one, and a second *gate* would be as much of a
 * divergence as a second store. Three Publisher URLs (the dashboard, the release
 * workflow, a release's detail page) sit under one check, so a pasted link into any of
 * them is covered and a new Publisher screen inherits the gate by being added inside it.
 *
 * NOT A SECURITY BOUNDARY. It hides a screen. The rule that actually matters — that a
 * non-entitled account cannot submit a release — is enforced separately in
 * api/publisherApi.submitRelease, which refuses with `no-entitlement` no matter which
 * screen called it. So this gate being bypassed from devtools does not open a path to
 * distribution.
 */
function RequirePublisher() {
  const publisher = useEntitlementsStore((s) => s.publisher);
  // A render, not a redirect: the URL the user clicked is the URL they keep.
  return <EntitlementGate owned={publisher} locked={<PublisherLockedRoute />} name="Publisher" />;
}

/** The locked Publisher screen, wired to the purchase flow. */
function PublisherLockedRoute() {
  const navigate = useNavigate();
  return <PublisherLockedState onGetPublisher={() => navigate(paths.upgrade)} />;
}

/**
 * Gate for the two AI creation methods Lyrixa Composer+ sells (OTO 2).
 *
 * WHAT WAS WRONG. Composer+ has been in the product catalogue since the prototype,
 * listing "Music DNA Analysis" and "Genre Mixer" as the two things it unlocks, and
 * `composerPlus` has been in the entitlement store the whole time — but nothing read it.
 * Both screens generated songs, and charged credits for them, for every account. This is
 * the check that was missing.
 *
 * ONE CHECK FOR BOTH ROUTES, and here rather than in the two screens, for the reasons
 * RequireStudio sets out at length: the sidebar rows, the three method cards at the top of
 * /create, the "Music DNA" and "Genre Mixer" links inside each other's screens and a
 * pasted URL all arrive at these two paths, and the last of those is not covered by any
 * control-level check at all. So the refusal lives at the destination.
 *
 * A RENDER, NOT A REDIRECT — same as the other two gates. Someone who pasted /dna keeps
 * /dna in the address bar and is told what /dna needs, rather than finding themselves on
 * a shopping page with no explanation of what it replaced.
 *
 * NOT A SECURITY BOUNDARY. It decides which screen renders. The rules the task actually
 * turns on — that no credit is spent and no generation is started without the plan — are
 * enforced in hooks/useSongs.createSong, which refuses before it charges no matter which
 * screen called it, exactly as api/publisherApi.submitRelease does for the Publisher. So
 * this gate being edited away in devtools does not buy anybody a song.
 */
function RequireComposerPlus() {
  const composerPlus = useEntitlementsStore((s) => s.composerPlus);
  return (
    <EntitlementGate owned={composerPlus} locked={<ComposerPlusLockedRoute />} name="Composer+" />
  );
}

/** Reseller ownership is both hidden in navigation and enforced at the destination. */
function RequireReseller() {
  const reseller = useEntitlementsStore((state) => state.reseller);
  return (
    <EntitlementGate
      owned={reseller}
      locked={<Navigate to={paths.upgrade} replace />}
      name="Reseller"
    />
  );
}

/**
 * The locked Composer+ screen, wired to the purchase flow and to whichever of the two
 * URLs the visitor is on.
 *
 * The path is read here rather than passed in as a prop from two separate wrappers,
 * because two wrappers would mean two `composerPlus` reads — and the point of the gate
 * above is that there is one. What the path decides is only which heading to show: the
 * locked screen keeps the name of the screen it stands in for, so the answer to "where am
 * I" survives the refusal.
 */
function ComposerPlusLockedRoute() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <ComposerPlusLockedState
      feature={pathname === paths.genreMixer ? 'mixer' : 'dna'}
      onGetComposerPlus={() => navigate(paths.upgrade)}
    />
  );
}

function CreateLockedRoute() {
  const navigate = useNavigate();
  return (
    <VerifiedAccessLockedState
      title="Create"
      subtitle="Four ways to turn an idea into a song."
      description="Create is available after your account is verified for app access. Upgrade keeps the same shape here: the page stays in place and the unlock path is the standard Products & Billing screen."
      icon={CirclePlus}
      onUpgrade={() => navigate(paths.upgrade)}
    />
  );
}

function LibraryLockedRoute() {
  const navigate = useNavigate();
  return (
    <VerifiedAccessLockedState
      title="Library"
      subtitle="Your tracks and playlists."
      description="Your Library becomes available after your account is verified for app access. Use the standard upgrade flow to unlock the rest of the app."
      icon={BadgeCheck}
      onUpgrade={() => navigate(paths.upgrade)}
    />
  );
}

function ImageMusicLockedRoute() {
  const navigate = useNavigate();
  return (
    <VerifiedAccessLockedState
      title="Image Music"
      subtitle="Upload a picture and describe the music it should become."
      description="Image Music stays in its own route and heading, but requires a verified account before it can be used."
      icon={ImageIcon}
      onUpgrade={() => navigate(paths.upgrade)}
    />
  );
}

function LicenseLockedRoute() {
  const navigate = useNavigate();
  return <LicenseLockedState onGetLicense={() => navigate(paths.upgrade)} />;
}

function VaultLockedRoute() {
  const navigate = useNavigate();
  return (
    <VerifiedAccessLockedState
      title="DFY Producer Vault"
      subtitle="A premium resource vault of prompt packs, genre presets, structures and hooks."
      description="The Vault keeps the same destination and heading, but requires a verified account before its resources are available."
      icon={Package}
      onUpgrade={() => navigate(paths.upgrade)}
    />
  );
}

function BonusLockedRoute() {
  const navigate = useNavigate();
  return (
    <VerifiedAccessLockedState
      title="Lyrixsa Bonus"
      subtitle="Extras included with your plan — claim them any time"
      description="Bonus content follows the same locked-route pattern as the rest of the app: the destination stays put and the upgrade path stays on the standard Products & Billing page."
      icon={Gift}
      onUpgrade={() => navigate(paths.upgrade)}
    />
  );
}

/** Keeps a signed-in visitor from landing back on the login screen. */
function LoginRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Login />;
  return <Navigate to={paths.dashboard} replace />;
}

/** Public support keeps the app shell for signed-in navigation and supplies its own frame otherwise. */
function SupportLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (isAuthenticated) return <AppShell />;

  return (
    <main className="lyx-public-guide-page">
      <header style={{ display: 'flex', alignItems: 'center', marginBottom: 22 }}>
        <Logo size={26} />
      </header>
      <Outlet />
    </main>
  );
}

export default function AppRoutes() {
  return (
    <Routes>
      {/* ---- Public. No shell, no auth. ---------------------------------- */}
      <Route path={paths.login} element={<LoginRoute />} />
      <Route path={paths.resetPassword} element={<ResetPassword />} />
      <Route path={paths.authCallback} element={<AuthCallback />} />
      <Route path={paths.verifyEmail} element={<VerifyEmail />} />
      <Route path={paths.verifyEmailPending} element={<VerifyEmailPending />} />
      <Route path={paths.verifyLicense} element={<LicenseVerify />} />
      <Route path="/license/verify/:licenseId" element={<LicenseVerify />} />
      {/* A shared track. Public because the recipient is, by definition, someone
          else — see note 1 above. */}
      <Route path="/share/:slug" element={<Share />} />
      {/*
        THE LEGAL DOCUMENTS. Public for the same reason /license/verify is, taken
        one step further: a terms page is not merely useful to someone without an
        account, it is *required* to be readable by them — the signup form asks them to
        accept it before the account exists, and a document behind the gate it guards
        cannot be read before agreeing to it.

        Explicit entries rather than /legal/:id, so `/terms` and `/privacy` are the
        conventional URLs people type and link to. One component behind them (see
        routes/Legal.tsx) so they cannot drift apart.
      */}
      <Route path={paths.terms} element={<Legal id="terms" />} />
      <Route path={paths.privacy} element={<Legal id="privacy" />} />
      <Route path={paths.licenseAgreement} element={<Legal id="license-agreement" />} />
      <Route path={paths.visualGuide} element={<VisualGuide />} />
      <Route path="/visual-guide" element={<Navigate to={paths.visualGuide} replace />} />
      <Route element={<SupportLayout />}>
        <Route path={paths.support} element={<Support />} />
        <Route path="/support/:topicId" element={<SupportTopic />} />
        <Route path={paths.ticket} element={<Ticket />} />
      </Route>

      {/* ---- Authenticated app ------------------------------------------- */}
      <Route element={<RequireAuth />}>
        <Route element={<RequireAppAccess />}>
          {/*
            The step after signup. Inside the gate, not beside /login: the account exists
            and is signed in by the time this renders, so it is a screen in the app rather
            than a fourth public page — and a visitor who is not signed in bounces to
            /login like every other authenticated route.

            NOT enforced, and deliberately: nothing redirects an account here or checks
            whether it has been seen. The signup handler navigates here once (see
            routes/Login.tsx) and both buttons leave for the Dashboard. Making it
            mandatory would need a "completed onboarding" flag, and a flag nothing can
            persist — there is no backend — would trap a reloading user on this screen
            forever. Reachable again by URL, which costs nothing: it is the billing page's
            products with a friendlier heading.
          */}
          <Route path={paths.products} element={<Products />} />
          <Route path={paths.upgrade} element={<Products />} />
          <Route path={paths.onboarding} element={<Onboarding />} />
          <Route path={paths.dashboard} element={<Dashboard />} />
          <Route path={paths.explore} element={<Explore />} />
          <Route element={<RequireVerifiedAccess locked={<CreateLockedRoute />} />}>
            <Route path={paths.create} element={<Create />} />
          </Route>

          <Route element={<RequireVerifiedAccess locked={<LibraryLockedRoute />} />}>
            <Route path={paths.library} element={<Library />} />
          </Route>
          {/*
            The two Studio URLs, both behind the entitlement gate.

            Grouped under one pathless <Route> so there is a single place that decides
            whether the Studio opens: the workspace at /library/:songId, and /studio,
            which only exists to resolve which song that is. Gating the resolver as well
            matters — without it, choosing "Lyrixa Studio" would redirect into the
            workspace and get refused there, which reads as a broken link rather than as
            a feature the account has not bought.

            The workspace is a sibling of the Library, not a child of it — see the note at
            the top of this file. The order below is unchanged; only the wrapper is new.
          */}
          <Route element={<RequireVerifiedAccess locked={<StudioLockedRoute />} />}>
            <Route element={<RequireStudio />}>
              <Route path="/library/:songId" element={<Studio />} />
              {/*
                The Studio's own entry point, and no longer a redirect to the Library.

                Choosing "Lyrixa Studio" used to land on the tracks list, so reaching the
                workspace took a second click; the Library is now its own destination with
                its own sidebar row, and this route opens the Studio directly. It still has
                to answer "on which song" — the workspace is /library/:songId and there is
                nothing to work on without a track — so StudioEntry resolves that (the song
                you were last on, else the newest) and hands over. See routes/StudioEntry.tsx.
              */}
              <Route path={paths.studio} element={<StudioEntry />} />
            </Route>
          </Route>

          {/* The library's second view and one playlist. Siblings of /library rather
              than children, because /library/:songId already claims everything one
              segment deep — see the note in routes/paths.ts. */}
          <Route element={<RequireVerifiedAccess locked={<LibraryLockedRoute />} />}>
            <Route path={paths.playlists} element={<Playlists />} />
            <Route path="/playlists/:playlistId" element={<Playlist />} />
          </Route>

          {/*
            THE PUBLISHER — a standalone product area, not a Studio tool.

            Three sibling routes under one entitlement gate. §4 forbids placing the
            Publisher inside the Studio, inside the Library, or in a drawer, so these are
            top-level paths with their own nav row rather than children of anything.

            `releases/new` is declared BEFORE `releases/:releaseId`, which is the one
            ordering detail that matters here: React Router ranks static segments above
            dynamic ones so it would resolve correctly either way, but writing it in this
            order means nobody has to know that to read it.
          */}
          <Route element={<RequireVerifiedAccess locked={<PublisherLockedRoute />} />}>
            <Route element={<RequirePublisher />}>
              <Route path={paths.publisher} element={<PublisherEntry />} />
              <Route path={paths.publisherNewRelease} element={<PublisherReleaseWizard />} />
              <Route path="/publisher/releases/:releaseId" element={<PublisherRelease />} />
            </Route>
          </Route>

          <Route element={<RequireVerifiedAccess locked={<VaultLockedRoute />} />}>
            <Route path={paths.vault} element={<Vault />} />
          </Route>
          {/*
            THE TWO COMPOSER+ CREATION METHODS, both behind one entitlement gate.

            Grouped under a pathless <Route> exactly as the Studio's two URLs and the
            Publisher's three are, so there is a single place that decides whether either
            opens — and so a third Composer+ feature inherits the gate by being added inside
            it rather than by somebody remembering to add a check.

            The two entries themselves are unchanged, and so is their order: only the
            wrapper is new. Neither screen's layout is touched — see components/studio/
            ComposerPlusLockedState.tsx, which stands in for the whole screen rather than
            altering it.
          */}
          <Route element={<RequireVerifiedAccess locked={<ComposerPlusLockedRoute />} />}>
            <Route element={<RequireComposerPlus />}>
              <Route path={paths.dna} element={<MusicDNA />} />
              <Route path={paths.genreMixer} element={<GenreMixer />} />
            </Route>
          </Route>
          {/* The third creation method keeps its own route and heading, and for unverified
              accounts now follows the same in-place locked-route pattern as the rest of the
              non-home destinations. */}
          <Route element={<RequireVerifiedAccess locked={<ImageMusicLockedRoute />} />}>
            <Route path={paths.imageMusic} element={<ImageMusic />} />
          </Route>
          <Route path={paths.voiceCloner} element={<VoiceCloner />} />
          <Route element={<RequireVerifiedAccess locked={<LicenseLockedRoute />} />}>
            <Route path={paths.licenses} element={<Licenses />} />
            <Route path={paths.agreement} element={<Agreement />} />
          </Route>
          <Route element={<RequireVerifiedAccess locked={<LicenseLockedRoute />} />}>
            <Route element={<RequireReseller />}>
              <Route path={paths.reseller} element={<Reseller />} />
            </Route>
          </Route>
          <Route path={paths.settings} element={<Settings />} />
          <Route path={paths.settingsProfile} element={<Navigate to={paths.settings} replace />} />
          <Route path={paths.settingsBilling} element={<Navigate to={paths.settings} replace />} />
          <Route path={paths.settingsSecurity} element={<Navigate to={paths.settings} replace />} />
          <Route
            path={paths.admin}
            element={
              <RequireAdmin>
                <Admin />
              </RequireAdmin>
            }
          >
            <Route path={paths.adminSupport} element={<AdminSupportInbox />} />
            <Route path={paths.adminUsers} element={<AdminUsers />} />
            <Route path={paths.adminArchive} element={<AdminArchive />} />
            <Route path={paths.adminMail} element={<AdminMail />} />
            <Route path={paths.adminBonuses} element={<AdminBonuses />} />
            <Route path={paths.adminVault} element={<AdminVault />} />
          </Route>
          <Route path={paths.topup} element={<Topup />} />
          <Route path={paths.guide} element={<QuickGuide />} />
          <Route element={<RequireVerifiedAccess locked={<BonusLockedRoute />} />}>
            <Route path={paths.bonus} element={<Bonus />} />
          </Route>

          {/*
            §1 lists /templates. There is NO templates screen anywhere in the
            prototype and no template fixture data — the nearest things are the
            Vault's prompt packs (entitlement-locked) and Create's genre chips.
            Rather than invent a page, this lands on Create, where a prompt is
            written. Flagged in the Phase 2 report; building it is a design decision,
            not a refactoring one.
          */}
          <Route path={paths.templates} element={<Navigate to={paths.create} replace />} />

          {/* Bare / lands on the Dashboard, as the prototype's initial view did. */}
          <Route index element={<Navigate to={paths.dashboard} replace />} />
        </Route>
      </Route>

      {/*
        Unknown URL. The prototype had no 404 — an unrecognised view string
        rendered nothing at all — so rather than design an error page, an unknown
        path goes home.
      */}
      <Route path="*" element={<Navigate to={paths.dashboard} replace />} />
    </Routes>
  );
}
